import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LicenseService } from '../../firebase/services/licenseService';
import { Timestamp } from 'firebase/firestore';

interface LicensePlan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  siteLimit: number;
  popular?: boolean;
}

const licensePlans: LicensePlan[] = [
  {
    id: 'social-pro',
    name: 'Lava Social Pro',
    price: 29,
    description: 'Complete social media integration suite',
    siteLimit: 1,
    features: [
      'YouTube Feed Pro',
      'Twitch Feed Pro', 
      'Instagram Feed',
      'TikTok Feed'
    ]
  },
  {
    id: 'layout-pro',
    name: 'Lava Layout Pro',
    price: 19,
    description: 'Advanced layout and design tools',
    siteLimit: 1,
    features: [
      'Advanced Carousel',
      'Advanced Blog Grid',
      'Advanced Heading',
      'Masonry Layouts'
    ]
  },
  {
    id: 'ecommerce-pro',
    name: 'Lava E-commerce Pro',
    price: 39,
    description: 'Complete e-commerce functionality',
    siteLimit: 1,
    features: [
      'Product Grids',
      'Shopping Carts',
      'Wishlist Widgets',
      'Checkout Integration'
    ]
  },
  {
    id: 'complete',
    name: 'Lava Complete',
    price: 79,
    description: 'Everything you need for a complete website',
    siteLimit: 5,
    features: [
      'All Social Features',
      'All Layout Features',
      'All E-commerce Features',
      'Priority Support'
    ],
    popular: true
  }
];

export default function PurchaseLicense() {
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const handlePurchase = async (planId: string) => {
    if (!currentUser) {
      alert('Please log in to purchase a license');
      return;
    }

    setLoading(true);
    try {
      // Find the selected plan
      const selectedPlan = licensePlans.find(plan => plan.id === planId);
      if (!selectedPlan) {
        throw new Error('Invalid plan selected');
      }

      // Create license data
      const purchaseDate = Timestamp.now();
      const expiryDate = new Date();
      expiryDate.setFullYear(expiryDate.getFullYear() + 1); // 1 year from now

      const licenseData = {
        userId: currentUser.uid,
        licenseKey: LicenseService.generateLicenseKey(),
        licenseType: planId as 'social-pro' | 'layout-pro' | 'ecommerce-pro' | 'complete',
        status: 'active' as const,
        purchaseDate,
        expiryDate: Timestamp.fromDate(expiryDate),
        sites: [],
        maxSites: selectedPlan.siteLimit,
        price: selectedPlan.price,
        transactionId: `demo-${Date.now()}` // In real app, this would be from payment processor
      };

      // Create the license in the database
      const licenseId = await LicenseService.createLicense(licenseData);
      
      console.log(`License created successfully: ${licenseId}`);
      
      alert('Purchase successful! Your license has been created and is now active.');
      navigate('/licenses');
      
    } catch (error) {
      console.error('Purchase failed:', error);
      alert(`Purchase failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Choose Your Lava Addons License
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            Select the perfect license for your WordPress website needs
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-2 xl:grid-cols-4">
          {licensePlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-lg shadow-lg overflow-hidden ${
                plan.popular ? 'ring-2 ring-indigo-500' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-indigo-500 text-white text-center py-2 text-sm font-medium">
                  Most Popular
                </div>
              )}
              
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                <p className="mt-2 text-gray-600">{plan.description}</p>
                
                <div className="mt-4">
                  <span className="text-3xl font-extrabold text-gray-900">${plan.price}</span>
                  <span className="text-gray-600">/year</span>
                </div>

                <div className="mt-2">
                  <span className="text-sm text-gray-500">
                    {plan.siteLimit === 1 ? '1 site' : `${plan.siteLimit} sites`}
                  </span>
                </div>

                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="ml-3 text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePurchase(plan.id)}
                  disabled={loading}
                  className={`mt-8 w-full py-3 px-4 rounded-md text-sm font-medium text-white ${
                    plan.popular
                      ? 'bg-indigo-600 hover:bg-indigo-700'
                      : 'bg-gray-600 hover:bg-gray-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50`}
                >
                  {loading ? 'Processing...' : `Purchase ${plan.name}`}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-2">License Information</h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p>• Individual licenses are valid for 1 website domain</p>
            <p>• Lava Complete license covers up to 5 websites</p>
            <p>• Licenses include 1 year of updates and support</p>
            <p>• Need more sites? Contact us for custom pricing</p>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Need a custom license or have questions?{' '}
            <a href="mailto:support@lavaaddons.com" className="text-indigo-600 hover:text-indigo-500">
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
} 